import { Anchor } from "@svea/design-system-components-react";
import { Stack, useText } from "@sveabank/cc-common";
import { useEffect, useState } from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router";
import DeleteApplicationModal from "../components/delete-application-modal";
import Header from "../components/header";
import PageContainer from "../components/page-container";
import { useDashboard } from "../features/dashboard/hooks";
import MissingCreditAssessment from "../features/error-modal/missing-credit-assessment";
import Navbar from "../features/navbar";
import MobileNavBar from "../features/navbar/mobileNavStepper";
import PageFooter from "../features/page-footer";
import TopNavigation from "../features/top-navigation";
import { useApplicationDetails } from "../hooks/application";
import { useApplicationId } from "../hooks/useApplicationId";
import { usePages } from "../hooks/usePages";
import { useSteps } from "../hooks/useSteps";
import { applicationsApiSlice, useDeleteApplicationMutation } from "../services/application";
import { resetPossibleGuarantors } from "../slices/guarantorSlice";
import { useAppDispatch } from "../store";
import { getRevision } from "../utils/storage";
import HeadingLayout from "./heading-layout";
import SignWarningModal from "./sign-warning-modal";

const ApplicationLayout = () => {
	const location = useLocation();
	const { isCurrentUserApplicant, isLoading: applicationDetailsIsLoading } =
		useApplicationDetails();
	const navigate = useNavigate();
	const { steps, areStepsCompleted } = useSteps();
	const { pages, resumeToPage, isLoading: pagesIsLoading } = usePages();
	const dispatch = useAppDispatch();
	const applicationId = useApplicationId();
	const [dialogOpen, setDialogOpen] = useState(false);
	const [dangerousEditing, setDangerousEdition] = useState(false);
	const { applications } = useDashboard(); // todo: don't load directly like this
	const [t, texts] = useText();
	const [deleteApplication, { isLoading: isDeletingApplication }] = useDeleteApplicationMutation();

	const redirectToStep = location.state?.redirectToStep;
	useEffect(() => {
		if (redirectToStep && resumeToPage && steps && !pagesIsLoading) {
			navigate(resumeToPage, { replace: true });
		}
	}, [redirectToStep, steps, resumeToPage, pagesIsLoading]);

	useEffect(() => {
		const revision = getRevision();

		if (!revision)
			dispatch(applicationsApiSlice.endpoints.fetchApplicationRevision.initiate(applicationId));
	}, []);

	useEffect(() => {
		setDangerousEdition(false);
	}, [location.pathname]);

	useEffect(() => {
		if (window.location.hash) {
			history.pushState("", document.title, window.location.pathname);
		}
	}, [window.location.hash]);

	useEffect(() => {
		const pageIsLocked = !!pages.find((page) => page.url === location.pathname)?.locked;
		if (pageIsLocked && !dangerousEditing) {
			setDialogOpen(true);
		}
	}, [location.pathname]);

	useEffect(() => {
		if (!isCurrentUserApplicant && !applicationDetailsIsLoading)
			navigate(`/summary/${applicationId}`);
	}, [isCurrentUserApplicant, applicationDetailsIsLoading]);

	const currentApplication = applicationId
		? applications.find((application) => application.applicationId === applicationId)
		: undefined;

	const isOngoingApplication = currentApplication?.status === "Ongoing";

	const allowNavigation = isOngoingApplication && isCurrentUserApplicant;

	const { orgNrDisplayName, name } = currentApplication || {};

	const handleDialogCancel = () => {
		if (resumeToPage) navigate(resumeToPage);
		else {
			const firstPage = pages[0];
			if (!firstPage) throw new Error("No first page found");
			navigate(firstPage.url);
		}
		setDialogOpen(false);
	};

	const handleDialogContinue = () => {
		setDangerousEdition(true);
		setDialogOpen(false);
	};

	const handleDeleteApplicationClick = async () => {
		await deleteApplication(applicationId).unwrap();
		dispatch(resetPossibleGuarantors());
		navigate("dashboard");
	};

	const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

	return (
		<>
			<Header>
				<Stack gap={8} direction="row" grow={false}>
					<Anchor
						as={Link}
						to={t(texts.header.helpLink)}
						target="_blank"
						iconEnd="externalLink"
						hasUnderline
					>
						{t(texts.header.help)}
					</Anchor>
					<TopNavigation
						abortApplication={() => {
							setIsDeleteModalOpen(true);
						}}
						allowNavigation={allowNavigation || false}
						companyName={name}
						orgNr={orgNrDisplayName}
					/>
				</Stack>
			</Header>

			{allowNavigation && <MobileNavBar />}
			{areStepsCompleted("CreditScoreStep") && <HeadingLayout />}
			<PageContainer>
				{allowNavigation && areStepsCompleted("CreditScoreStep") && <Navbar />}
				{areStepsCompleted("CreditScoreStep") && <Outlet />}
			</PageContainer>
			<PageFooter />

			<MissingCreditAssessment open={!areStepsCompleted("CreditScoreStep")} />
			<SignWarningModal
				isOpen={dialogOpen}
				onCancelClick={handleDialogCancel}
				onContinueClick={handleDialogContinue}
			/>

			<DeleteApplicationModal
				open={isDeleteModalOpen}
				setOpen={setIsDeleteModalOpen}
				onDelete={() => handleDeleteApplicationClick()}
				loading={!!isDeletingApplication}
			/>
		</>
	);
};

export default ApplicationLayout;
